import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import { useAuth } from './hooks/useAuth';
import { Analytics } from '@vercel/analytics/react';
import { SpeedInsights } from '@vercel/speed-insights/react';
import { trackPageView } from './utils/analytics';

// Components  
import LandingPage from './components/Landing/LandingPage';
import Login from './components/Auth/Login';
import Dashboard from './components/Dashboard/Dashboard';
import ProjectPage from './components/Project/ProjectPage';
import NewTest from './components/Test/NewTest';
import TestDetailsPage from './components/TestDetails/TestDetailsPage';
import Layout from './components/Layout/Layout';
import BlogList from './components/Blog/BlogList';
import BlogPost from './components/Blog/BlogPost';
import TermsOfService from './components/Legal/TermsOfService';
import PrivacyPolicy from './components/Legal/PrivacyPolicy';
import RefundPolicy from './components/Legal/RefundPolicy';
import FAQPage from './components/FAQ/FAQPage';
import GlossaryPage from './components/Glossary/GlossaryPage';
import ApiDocsPage from './components/ApiDocs/ApiDocsPage';

// Protected Route Component  
const ProtectedRoute = ({ children }) => {
    const { user, loading } = useAuth();

    if (loading) {
        return (
            <div className="loader-container">
                <div className="loader"></div>
            </div>
        );
    }

    if (!user) {
        return <Navigate to="/login" />;
    }

    return <Layout>{children}</Layout>;
};

// Public Route Component (redirect if authenticated)  
const PublicRoute = ({ children }) => {
    const { user, loading } = useAuth();

    if (loading) {
        return (
            <div className="loader-container">
                <div className="loader"></div>
            </div>
        );
    }

    if (user) {
        return <Navigate to="/dashboard" />;
    }

    return children;
};

// GA4 SPA page view tracker
const GAPageViewTracker = () => {
    const location = useLocation();

    useEffect(() => {
        trackPageView(location.pathname + location.search, document.title);
    }, [location]);

    return null;
};

function App() {
    return (
        <AuthProvider>
            <Router>
                <GAPageViewTracker />
                <Analytics />
                <SpeedInsights />
                <Routes>
                    <Route path="/" element={<LandingPage />} />
                    <Route
                        path="/login"
                        element={
                            <PublicRoute>
                                <Login />
                            </PublicRoute>
                        }
                    />
                    <Route
                        path="/dashboard"
                        element={
                            <ProtectedRoute>
                                <Dashboard />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/project/:projectId"
                        element={
                            <ProtectedRoute>
                                <ProjectPage />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/project/:projectId/test/new"
                        element={
                            <ProtectedRoute>
                                <NewTest />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/endpoint/:endpointId"
                        element={
                            <ProtectedRoute>
                                <TestDetailsPage />
                            </ProtectedRoute>
                        }
                    />
                    <Route path="/blog" element={<BlogList />} />
                    <Route path="/blog/:slug" element={<BlogPost />} />
                    <Route path="/terms" element={<TermsOfService />} />
                    <Route path="/privacy" element={<PrivacyPolicy />} />
                    <Route path="/refund" element={<RefundPolicy />} />
                    <Route path="/faq" element={<FAQPage />} />
                    <Route path="/glossary" element={<GlossaryPage />} />
                    <Route path="/api-docs" element={<ApiDocsPage />} />
                    <Route path="*" element={<Navigate to="/" />} />
                </Routes>
            </Router>
        </AuthProvider>
    );
}

export default App;

